import React from "react";

/**
 * ModalShell — Warm Precision design-system primitive for full-screen modal panels.
 *
 * Pattern: a centered card with rounded corners + shadow that hosts its own
 * header / body / footer regions (unlike SectionCard which is content-only).
 *
 * Why this is a separate primitive: SectionCard wraps a single content block
 * with internal padding and is designed for in-page sections. Modals need
 * `flex flex-col`, `max-h-[90vh]`, and an overflow-scroll body — wrapping that
 * inside SectionCard fights the layout. ModalShell owns the shell so pages
 * can stop hand-rolling `bg-white rounded-xl shadow-2xl …` divs.
 *
 * Usage:
 *   <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 *     <ModalShell size="lg">
 *       <ModalShell.Header title="Edit campaign" onClose={onClose} />
 *       <ModalShell.Body>…</ModalShell.Body>
 *       <ModalShell.Footer>
 *         <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
 *         <PrimaryButton onClick={save}>Save</PrimaryButton>
 *       </ModalShell.Footer>
 *     </ModalShell>
 *   </div>
 */

type Size = "sm" | "md" | "lg" | "xl" | "2xl" | "5xl";

const SIZE: Record<Size, string> = {
  sm:  "max-w-sm",
  md:  "max-w-md",
  lg:  "max-w-lg",
  xl:  "max-w-2xl",
  "2xl": "max-w-4xl",
  "5xl": "max-w-5xl",
};

interface ModalShellProps {
  size?: Size;
  height?: "auto" | "tall" | "full";
  className?: string;
  children?: React.ReactNode;
}

function ModalShell({ size = "md", height = "auto", className = "", children }: ModalShellProps) {
  const sizeCls = SIZE[size];
  const heightCls =
    height === "full" ? "h-[96vh]" :
    height === "tall" ? "max-h-[90vh]" :
    "max-h-[90vh]";
  return (
    <div className={`rounded-2xl bg-white shadow-2xl w-full ${sizeCls} ${heightCls} flex flex-col overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

function Header({
  title,
  description,
  onClose,
  children,
}: {
  title?: string;
  description?: string;
  onClose?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="px-6 py-4 border-b border-zinc-200 flex items-start justify-between gap-4 shrink-0">
      <div className="min-w-0">
        {title && <h3 className="font-semibold text-[15px] text-zinc-800 leading-snug truncate">{title}</h3>}
        {description && <p className="text-sm text-zinc-500 mt-0.5">{description}</p>}
        {children}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 text-zinc-400 hover:text-zinc-700 text-xl leading-none"
        >
          &times;
        </button>
      )}
    </div>
  );
}

function Body({ className = "", children }: { className?: string; children?: React.ReactNode }) {
  return <div className={`flex-1 overflow-y-auto px-6 py-4 ${className}`}>{children}</div>;
}

function Footer({ className = "", children }: { className?: string; children?: React.ReactNode }) {
  return (
    <div className={`px-6 py-3 border-t border-zinc-200 flex justify-end gap-2 shrink-0 ${className}`}>
      {children}
    </div>
  );
}

ModalShell.Header = Header;
ModalShell.Body = Body;
ModalShell.Footer = Footer;

export default ModalShell;
